import React from 'react';
import {useRecoilState} from "recoil";
import {Link as RouterLink} from "react-router-dom";
import {
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress, FormControlLabel,
    Link,
    Stack,
    Switch,
    Tooltip,
    Typography
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {useTranslation} from "react-i18next";
import {includePublicCollectionsState, makeRequest, rolesState, subState} from "../../services/requestService";
import {API_URL} from "../../constants";
import HomeLayout from "../../components/HomeLayout/HomeLayout";
import CollectionDialog from "../../components/Collection/CollectionDialog";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

// Selects the tab of the type associated with the domain name used
const defaultTab = () => {
    return "ALL";
}

const LoadingState = {
    UNSET: 0b000, // 0
    LOADED: 0b001,
    LOADING_COLLECTIONS: 0b010, // 2
    COLLECTIONS_LOADED: 0b011, // 3
    LOADING_TITLES: 0b110, // 6
    TITLES_LOADED: 0b111, // 7
    has(state, search) {
        return state & search !== 0
    }
}

async function fetchCollections(includePublic = false) {
    const response = await makeRequest(`${API_URL}/user/collections?includePublic=${includePublic}`);

    if (response.status !== 200) {
        return [];
    }

    return response.data.collections;
}

export default function Home() {
    const {t} = useTranslation('common');
    const [sub,] = useRecoilState(subState);
    const [roles,] = useRecoilState(rolesState);
    const [totalPages, setTotalPages] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [state, setState] = React.useState(LoadingState.UNSET);
    const [searchValue, setSearchValue] = React.useState("");
    const [collections, setCollections] = React.useState([]);
    const [showCollectionDialog, setShowCollectionDialog] = React.useState(false);
    const [updateCollections, setUpdateCollections] = React.useState(0);
    const [includePublicCollections, setIncludePublicCollections] = useRecoilState(includePublicCollectionsState);

    React.useEffect(() => {
        document.title = t("main.title") + " - " + t("main.pages.home.title");
    }, [t]);

    React.useEffect(async () => {
        setState(LoadingState.LOADING_COLLECTIONS)
        const collections = await fetchCollections(includePublicCollections);
        setState(LoadingState.COLLECTIONS_LOADED);
        setCollections(collections);
    }, [sub, updateCollections, includePublicCollections]);

    const handleSubmitSearch = (search) => {
        setSearchValue(search);
    }

    const content = state === LoadingState.COLLECTIONS_LOADED ?
        collections.map((c) => (
            <Card sx={{
                mb: 2,
                p: 1
            }}>
                <Typography variant="h5" display="flex" alignItems="center" gap={1}>
                    <Link component={RouterLink} to={`/collection/${c.collection.id}${c.publicAccess ? '?publicAccess=true' : ''}`}>
                        {c.collection.name}
                    </Link>
                    <Chip
                    label={t(`main.collections.role.${c.role.toLowerCase()}`)}
                    variant="outlined"
                    sx={{textTransform: 'capitalized'}}/>
                    <Tooltip title={t(`main.components.form.collection.visibility.${c.collection.visibility?.toLowerCase()}`)} arrow>
                        {c.collection.visibility === "PRIVATE" ? (<LockIcon/>) : <LockOpenIcon/>}
                    </Tooltip>
                </Typography>
                <Typography>
                    {c.collection.description}
                </Typography>
            </Card>
        )) :
        [<Stack key={"loading-home"} alignItems="center" m={4}><CircularProgress size={80}/></Stack>];


    const aside = (
        <Card elevation={0}>
            <CardContent>
                <Typography color="textSecondary" display="flex" alignItems="center" gutterBottom>
                    <InfoIcon fontSize="small" sx={{mr: 1}}/>
                    {" " + t('main.pages.home.informations.title')}
                </Typography>
                <Typography variant="body2" component="p">
                    {t('main.pages.home.informations.content')}
                </Typography>
            </CardContent>
        </Card>
    );

    const tabs = React.useMemo(() => <></>, []);

    const title = (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography ml={2} variant="h6" fontSize={22} fontWeight="bold"
                        color="textSecondary">{t("main.pages.home.subtitle")}</Typography>
            {roles.includes("ROLE_ADMIN") &&
                <Button variant="contained" onClick={() => setShowCollectionDialog(true)}>
                    {t('main.pages.home.collection.new')}
                </Button>
            }
        </Stack>
    );

    return (
        <>
            <HomeLayout
                titleElement={title}
                content={content}
                aside={aside}
                forceDisplay={"list"}
                tabs={tabs}
                toolbarElms={<>
                    <FormControlLabel control={
                        <Switch  checked={includePublicCollections} onChange={(_, checked) => setIncludePublicCollections(checked)} />

                    } label="Collections publiques" />
                </>}
                // onScrollHitBottom={() => loadData(searchValue, tabValue, collectionIds, currentPage + 1)}
                onSubmit={handleSubmitSearch}
                noDataText={t("main.pages.home.collection.empty")}
                hasMoreData={state === LoadingState.TITLES_LOADED ? currentPage + 1 < totalPages : false}
            />
            <CollectionDialog
                mode="CREATE"
                open={showCollectionDialog}
                onClose={() => setShowCollectionDialog(false)}
                onCreated={() => {
                    setUpdateCollections(updateCollections + 1);
                    setShowCollectionDialog(false);
                }}
            />
        </>
    );
}
